import React, { useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { Page, Header } from 'components';
import validate from 'validate.js';
import { Button, Card, CardHeader, CardContent, CardActions, Grid, Divider, TextField, colors } from '@mui/material';
import DumpsDataTable from './RETFilesUploadHistory';
import { RETspExport, RETEntireNetworkspExport, SetFormState, LastDumpsUpdate } from '../../actions/RetAuditActions';

const useStyles = makeStyles(theme => ({
	root: {
		padding: theme.spacing(3)
	},
	container: {
		marginTop: theme.spacing(3)
	},
	tabs: {
		marginTop: theme.spacing(3)
	},
	content: {
		marginTop: theme.spacing(3)
	},
	paper: {
		padding: theme.spacing(1)
	},
	popover: {
		pointerEvents: 'none'
	},
	generate: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900]
		}
	}
}));

const schema = {
	sites: {
		presence: { allowEmpty: false, message: 'is required' },
	}
};

const RETEnablingChecks = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.CPMDumpExportReducer.formState);
	const dumps = useSelector(state => state.RETEnablingChecksReducer.dumps);
	const _getData = useCallback(() => {
		dispatch(LastDumpsUpdate());
	}, []);

	useEffect(() => {
		const errors = validate(formState.values, schema);
		_getData();
		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
		});
	}, [formState.values]);

	const _handleChange = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _handleDateChange = (value, name) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[name]: value,
			},
			touched: {
				...formState.touched,
				[name]: true,
			},
		});
	};

	const _hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));
	const user = useSelector(state => state.session.user);
	const _generate = () => {
		dispatch(RETspExport(formState.values, user.id));
	};

	const _generateEntireNetwork = () => {
		dispatch(RETEntireNetworkspExport(user.id));
	};

	return (
		<Page className={classes.root}>
			<Header module="On Demand Report" view="RET Enabling Checks" />
			<div className={clsx(classes.container)}>
				<Card className={classes.container}>
					<CardHeader title="Custom Site List" />
					<Divider />
					<CardContent>
						<Grid item md={12} xs={12}>
							<TextField
								fullWidth
								label="Site(s) List"
								name="sites"
								onChange={_handleChange}
								value={formState.values.sites}
								variant="outlined"
								error={_hasError('sites')}
								helperText={
									_hasError('sites') ? formState.errors.sites[0] : 'Example: 10500,10501,10502'
								}
							/>
						</Grid>
					</CardContent>
					<Divider />
					<CardActions>
						<Button className={classes.generate} onClick={_generate} variant="contained" disabled={!formState.isValid}>Generate</Button>
					</CardActions>
				</Card>
				<br />
				<Card>
					<CardHeader title="Entire Network" />
					<Divider />
					<CardContent>
						<Button className={classes.generate} onClick={_generateEntireNetwork} variant="contained">Generate</Button>
					</CardContent>
				</Card>
				<br />
				<Card>
					<CardHeader title="Latest Imported Files" />
					<Divider />
					<CardContent>
						<DumpsDataTable data={dumps} />
					</CardContent>
				</Card>
			</div>
		</Page>
	);
};

export default RETEnablingChecks;
